import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import "./style.scss";
import Plus from "../../../assets/Plus.svg";
import Minus from "../../../assets/Minus.svg";
import BalanceUp from "../../../assets/BalanceUp.svg";
import BalanceDown from "../../../assets/BalanceDown.svg";
import { useTranslation } from "react-i18next";
import {
  dashboardStore,
  getPositionAccrued,
  getPositionMarketPrice,
  IUserBrokerageAccount,
  useCurrencies,
} from "entities/dashboard";
import { useStore } from "zustand";
import { AccountItem } from "../components/accounts-card";
import { CURRENCY_FLAGS } from "static/currencies";
import { AddAccountCurrency } from "../components/add-account-currency";
import { ioRTStore } from "services/rt.socket";
import { AssetType } from "static/types";
import { PATH_LIST } from "shared/lib/react-router";
import { useLocation, useNavigate } from "react-router-dom";
import { PortfolioItem } from "../components/portfolio-card";
import { marketStore } from "entities/market";
import { formatPrice, getCurrSymbol, navigateToAsset } from "helpers/functions";
import { useBlockScrollbar } from "hooks/useBlockScrollbar";
import SwitchTabs from "widgets/switch-tabs";
import { Transaction } from "../components/activities-transactions";
import { accountStore } from "entities/account";
import InfiniteScroll from "react-infinite-scroll-component";
import { Spinner } from "widgets/loading";
import Open from "../../../assets/GoBack.svg";
import EmptyOrdersBox from "../components/empty-orders-box";
import useSubAccount from "hooks/useSubAccount";
import { userVerified } from "helpers/functions";

export const PortfolioPage = () => {
  const { t, i18n } = useTranslation("app");
  const navigate = useNavigate();
  const [addAccCurrency, setAddAccCurrency] = useState(false);
  const [loadPortfolio, setLoadPortfolio] = useState(false);
  const [hide, setHide] = useState(false);
  const {
    user_deposit_accounts,
    user_brokerage_accounts,
    user_accounts_show_config,
    loadUserAccounts,
  } = useStore(dashboardStore);
  const { subAccount } = useSubAccount();
  const currencies = useCurrencies();
  const location = useLocation();
  const scrollableContainerRef = useRef<HTMLDivElement>(null);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    {},
  );

  // * -  -  -  -

  const {
    isLoading,
    transactions,
    transactionsCount,
    activeTransactions,
    getTransactions,
    getActiveTransactions,
  } = useStore(accountStore);
  const [activePortfolioTab, setActivePortfolioTab] = useState("portfolio");

  const [page, setPage] = useState(0);
  const limit = 20;

  // todo fix the limit bug
  useEffect(() => {
    getActiveTransactions(0, 0);
  }, []);

  useEffect(() => {
    getTransactions(0, 0);
  }, [page]);

  const currItemsLength = (page + 1) * limit;
  const count = transactionsCount;

  // *  -  -  -  -

  useEffect(() => {
    // setLoadPortfolio(true);
    loadUserAccounts().then(() => {
      setLoadPortfolio(false);
    });
  }, []);

  const getUserAccount = useCallback(() => {
    return Object.entries(user_accounts_show_config)
      .filter(([key, value]) => !!value)
      .map(([key]) => {
        const acc = user_deposit_accounts.find(
          (acc) => acc.currency.code === key,
        );
        return {
          symbol: key,
          amount: acc?.amount || 0,
        };
      });
  }, [user_deposit_accounts, user_accounts_show_config]);

  const handleCloseModal = () => {
    setAddAccCurrency(false);
  };

  useBlockScrollbar(addAccCurrency, [addAccCurrency]);

  const { connected, list, subscribe, unsubscribe } = useStore(ioRTStore);
  const [activeOrderTab, setActiveOrderTab] = useState("active");

  const { activeAssetType: activeTab, sortConfigs: configs } =
    useStore(marketStore);

  const brokerageAccounts = useMemo(() => {
    return [
      ...user_brokerage_accounts
        .filter((a) => a.quantity > 0 || a.blocked_quantity > 0)
        .map((v) => ({
          ...v,
          quantity: v.quantity + v.blocked_quantity,
          nonTrading: false,
        })),
    ];
  }, [user_brokerage_accounts]);

  useEffect(() => {
    if (!connected) return;

    const symbols = brokerageAccounts
      .filter((a) => [AssetType.Stock, AssetType.ETF].includes(a.asset.type))
      .map((a) => {
        const ex = a.asset.exchange;
        return ex ? `${ex.symbol}~${a.asset.symbol}` : a.asset.symbol;
      });

    for (const s of symbols) {
      subscribe(s);
    }

    return () => {
      for (const s of symbols) {
        unsubscribe(s);
      }
    };
  }, [connected, brokerageAccounts]);

  const handleNavigateMarket = () => {
    navigate(PATH_LIST.market);
  };

  // Единый расчёт рыночной цены для главной и портфолио —
  // entities/dashboard/lib/position-price.ts. Раньше он был скопирован на оба
  // экрана и разошёлся: облигации оценивались по ask на одном и по середине
  // спреда на другом, из-за чего суммы портфеля по валюте не сходились.
  const getAssetPrice = useCallback(
    (account: IUserBrokerageAccount) => getPositionMarketPrice(account, list),
    [list],
  );

  const getAssetProgress = useCallback(
    (account: IUserBrokerageAccount) => {
      const { pnl, price } = account;
      const assetPrice = getAssetPrice(account);

      if (pnl) {
        const basePrice = price * pnl;
        const rtPnl = assetPrice / basePrice;
        return +((pnl * rtPnl - 1) * 100).toFixed(2);
      } else {
        const i = price / 100;
        const diff = +(assetPrice - price).toFixed(2);
        return +(diff / i).toFixed(2);
      }
    },
    [getAssetPrice],
  );

  const getUserBalance = useCallback(
    (currency: string) => {
      let balance = 0;
      let balanceByMarket = 0;
      let pnl = 0;

      const accounts = brokerageAccounts.filter(
        (a) => a.asset.currency.code === currency,
      );

      balance += accounts
        .map(({ price, quantity }) => price * quantity)
        .reduce((prev, curr) => prev + curr, 0);
      balanceByMarket += accounts
        .map((acc) => getAssetPrice(acc) * acc.quantity)
        .reduce((prev, curr) => prev + curr, 0);

      // НКД входит в сумму позиций, но не в P&L: balanceByMarket и balance ниже
      // остаются чистыми, иначе НКД попал бы в «прибыль» целиком.
      const accrued = accounts
        .map((acc) => getPositionAccrued(acc.asset, acc.quantity))
        .reduce((prev, curr) => prev + curr, 0);

      pnl = (balanceByMarket / balance - 1) * 100;

      const symbol = currency;
      const isUp = balanceByMarket >= balance;

      const diff = balanceByMarket - balance;
      const percent = +pnl.toFixed(2);
      const progress = +diff.toFixed(2);

      if (loadPortfolio && !balanceByMarket) {
        return <Spinner style={{ margin: "24px" }} />;
      }

      return (
        <div className="portfolio-balance-by-currency">
          <p>
            {symbol} {formatPrice(balanceByMarket + accrued) || 0}
          </p>

          {balanceByMarket && (
            <div>
              <p>P&L</p>
              <span className={`${isUp ? "" : "red"}`}>
                {isUp ? "+" : ""}
                {progress} {getCurrSymbol(symbol)}
              </span>
            </div>
          )}
        </div>
      );
    },
    [
      loadPortfolio,
      hide,
      user_deposit_accounts,
      brokerageAccounts,
      getAssetPrice,
    ],
  );

  const brokerageAccountsGroup = useMemo(() => {
    const groups: Record<string, any[]> = {};
    for (const acc of brokerageAccounts) {
      const curr = acc.asset.currency.code;
      if (!groups[curr]) {
        groups[curr] = [];
      }

      groups[curr].push(acc);
    }

    return groups;
  }, [brokerageAccounts]);

  const handleToggleGroup = (curr: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [curr]: !prev[curr],
    }));
  };

  const tabOptions = [
    { key: "portfolio", label: "portfolio" },
    { key: "orders", label: "orders" },
  ];

  useEffect(() => {
    if (location.state?.scrollToBottom) {
      if (scrollableContainerRef.current) {
        scrollableContainerRef.current.scrollTo({
          top: scrollableContainerRef.current.scrollHeight,
        });
      }
      window.history.replaceState(
        {
          ...window.history.state,
          usr: { ...location.state, scrollToBottom: false },
        },
        "",
      );
    }
  }, [activePortfolioTab, transactions.length, location.state]);

  const [transactionSheetOpen, setTransactionSheetOpen] = useState(false);

  const handleTransactionSheetToggle = (isOpen: boolean) => {
    setTransactionSheetOpen(isOpen);
  };

  const activeTransactionsAssets = useMemo(() => {
    return activeTransactions.filter(
      (t) => t.asset.type !== AssetType.Currency,
    );
  }, [activeTransactions]);

  const transactionsAssets = useMemo(() => {
    return transactions.filter((t) => t.asset.type !== AssetType.Currency);
  }, [transactions]);

  return (
    <div className="portfolio-nav-page">
      <SwitchTabs
        tabs={tabOptions}
        activeTabKey={activePortfolioTab}
        onClick={setActivePortfolioTab}
      />

      {activePortfolioTab === "portfolio" ? (
        <div
          key="portfolio"
          className="portfolio-nav-page__main"
          ref={scrollableContainerRef}
        >
          <div className="portfolio">
            <div>
              {loadPortfolio ? (
                <p className="assets-loading">{t("loading")}</p>
              ) : !brokerageAccounts.length ? (
                <div className="portfolio-cards">
                  <div className="empty-portfolio">
                    <p>{t("emptyPortfolio")}</p>
                    <button
                      className="go-to-market-btn"
                      onClick={handleNavigateMarket}
                    >
                      {t("goToMarket")}
                    </button>
                  </div>
                </div>
              ) : (
                Object.entries(brokerageAccountsGroup).map(
                  ([curr, accounts]) => {
                    const isExpanded = expandedGroups[curr];
                    const showAccounts = isExpanded
                      ? accounts
                      : accounts.slice(0, 3);
                    const hasMore = accounts.length > 3;
                    return (
                      <React.Fragment key={curr}>
                        {getUserBalance(curr)}

                        <div className="portfolio-cards">
                          {showAccounts.map((account) => (
                            <PortfolioItem
                              asset={account}
                              nonTrading={account.nonTrading}
                              key={account.asset.id}
                              tradePrice={+account.price.toFixed(4)}
                              price={getAssetPrice(account)}
                              progress={getAssetProgress(account)}
                              onClick={() => {
                                if (
                                  account.asset.type ===
                                  AssetType.StructuredProduct
                                ) {
                                  navigate(
                                    PATH_LIST.strategy(account.asset.id),
                                  );
                                } else {
                                  navigateToAsset(
                                    account.asset.type,
                                    account.asset.id,
                                    navigate,
                                  );
                                }
                              }}
                              receivedEarnings={account.receivedEarnings}
                              nextCouponDate={account.nextCouponDate}
                              nextCouponValue={account.nextCouponValue}
                            />
                          ))}
                        </div>

                        {hasMore && (
                          <button
                            className="show-more-portfolio-cards"
                            onClick={() => handleToggleGroup(curr)}
                          >
                            {/* {isExpanded ? t("showLess") : t("showMore")} */}
                            <img
                              style={{
                                rotate: isExpanded ? "90deg" : "-90deg",
                              }}
                              src={Open}
                              alt="Open"
                            />
                          </button>
                        )}
                      </React.Fragment>
                    );
                  },
                )
              )}
            </div>
          </div>

          <div className="accounts">
            <div className="accounts-container">
              <div className="accounts-header">
                <p>{t("accounts")}</p>

                <div>
                  {!subAccount && userVerified() && (
                    <button onClick={() => navigate(PATH_LIST.deposit)}>
                      {i18n.language === "ru" ? "Пополнить" : t("deposit")}
                    </button>
                  )}
                </div>
              </div>

              {loadPortfolio ? (
                <p className="assets-loading">{t("loading")}</p>
              ) : (
                <>
                  {getUserAccount().map(({ symbol, amount }, i) => (
                    <AccountItem
                      key={i}
                      img={CURRENCY_FLAGS[symbol]}
                      name={symbol}
                      price={+amount.toFixed(2)}
                      hidden={hide}
                      onClick={() => {
                        hide && setHide(false);
                      }}
                    />
                  ))}
                </>
              )}

              <button
                className="add-account-long-btn"
                onClick={() => setAddAccCurrency(true)}
              >
                <div className="add-account-btn-details">
                  <div className="details-icon"></div>
                  <div className="details-symbol"></div>
                </div>

                <img
                  className="details-plus"
                  src={
                    currencies.length == getUserAccount().length ? Minus : Plus
                  }
                  alt="Plus"
                />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div
          key="orders"
          className="portfolio-nav-page__main portfolio-nav-page__orders-section"
        >
          <div key={activeOrderTab} className="currencies-orders-section-tabs">
            <button
              className={`
                currencies-orders-section-tabs-tab
                ${
                  activeOrderTab === "active"
                    ? "currencies-orders-section-tabs-tab-active"
                    : ""
                }
                `}
              onClick={() => setActiveOrderTab("active")}
            >
              <p>{t("activeOrders")}</p>
              <span></span>
            </button>

            <button
              className={`
                currencies-orders-section-tabs-tab
                ${
                  activeOrderTab === "history"
                    ? "currencies-orders-section-tabs-tab-active"
                    : ""
                }
                `}
              onClick={() => setActiveOrderTab("history")}
            >
              <p>{t("history")}</p>
              <span></span>
            </button>
          </div>

          <div className="currencies-orders-section-content">
            {activeOrderTab === "active" ? (
              activeTransactionsAssets.length > 0 ? (
                activeTransactionsAssets.map((t: any, i) => (
                  <Transaction
                    key={i}
                    deal={t}
                    type="asset"
                    onSheetToggle={handleTransactionSheetToggle}
                  />
                ))
              ) : (
                <EmptyOrdersBox
                  title={t("noActiveOrders")}
                  description={t("noActiveOrdersMessage")}
                  buttonText={t("goToMarket")}
                  buttonOnClick={() => {
                    navigate(PATH_LIST.market);
                  }}
                />
              )
            ) : transactionsAssets.length > 0 ? (
              transactionsAssets.map((t: any, i) => (
                <Transaction
                  key={i}
                  deal={t}
                  type="asset"
                  onSheetToggle={handleTransactionSheetToggle}
                />
              ))
            ) : (
              <EmptyOrdersBox
                title={t("noOrderHistory")}
                description={t("noPastOrdersMessage")}
                buttonText={t("goToMarket")}
                buttonOnClick={() => {
                  navigate(PATH_LIST.market);
                }}
              />
            )}
          </div>

          {/* {!!activeTransactions.length && (
            <div className="portfolio-nav-page__main__widget">
              <div className="portfolio-nav-page__main__widget__header">
                <p>{t("pending")}</p>
              </div>
              {activeTransactions
                .filter((t) => t.asset.type !== AssetType.Currency)
                .map((t: any, i) => (
                  <Transaction key={i} deal={t} />
                ))}
            </div>
          )}

          {!!transactions.length && (
            <div
              className="portfolio-nav-page__main__widget"
              id="scrollable-container"
            >
              <div className="portfolio-nav-page__main__widget__header">
                <p>{t("history")}</p>
              </div>

              <InfiniteScroll
                dataLength={currItemsLength}
                next={() => {
                  setPage(page + 1);
                }}
                hasMore={currItemsLength < count}
                scrollThreshold={0.9}
                scrollableTarget={"scrollable-container"}
                loader={<p className="assets-loading">{t("loading")}</p>}
              >
                {transactions
                  .filter((t) => t.asset.type !== AssetType.Currency)
                  .map((t: any, i) => (
                    <Transaction key={i} deal={t} />
                  ))}
              </InfiniteScroll>
            </div>
          )} */}
        </div>
      )}

      {addAccCurrency && (
        <AddAccountCurrency onClose={() => handleCloseModal()} />
      )}
    </div>
  );
};
